import React, { useState, useRef } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { useQuery, useMutation } from '@apollo/client/react/hooks';

import { FormattedMessage } from 'react-intl';

import Styled from 'styled';
import SVGIcon from 'lib/images/svg-icon/svg-icon';
import btoa from 'lib/base64/btoa';

import newArticle from 'four-nets/icons/svg/icon_new-article.svg';
import refresh from 'four-nets/icons/svg/icon_refresh.svg';
import ActionButtonView from 'four-nets/ui/action-button/action-button-view';
import SearchInput from 'four-nets/ui-v2/search-input/search-input';
import CreateEditProductView from 'four-nets/bazaar/product/create-edit-product/create-edit-product-view';
import FilterView from 'four-nets/bazaar/filter/filter-view';
import FieldView from 'four-nets/bazaar/filter/field-view';
import { HideForUnpaidVendor } from 'four-nets/vendor/permissions';
import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';
import { FourNetsApi } from 'four-nets/api/api';
import { UserStore } from 'four-nets/user/user';
import { Site } from 'four-nets/site';
import { BazaarStore, SuperCategoryStore } from 'four-nets/bazaar/bazaar';
import { CreateEditProductViewModel } from 'four-nets/bazaar/product/create-edit-product/create-edit-product-view';

import tableActionButtonStyles from './table-action-button-styles.scss';
import tableActionButtonStylesDesktop from './table-action-button-styles-desktop.scss';
import searchInputStyles from './search-input-styles.scss';

import ProductsTableView from './products-table-view/products-table-view';
import query, { MyProductsQuery, MyProductsQueryVariables } from './query.graphql';
import deleteProductsMutation, {
    SellerDeleteProductsInput,
    SellerDeleteProductsMutationVariables,
} from './seller_delete_products.graphql';
import repostAllProductsMutation, { SellerRepostAllProductsMutation } from './seller-repost-all-products.graphql';
import viewerUnstarProductsMutation, {
    BuyerUnstarProductsMutation,
    BuyerUnstarProductsMutationVariables,
} from './viewer-unstar-products.graphql';

import { MyProductsStatusType, MyBazaarType } from './query.graphql.types';
import defaultStyles from './my-table-view.scss';

export interface Props extends RouteComponentProps {
    status?: MyProductsStatusType;
    myBazaarType: MyBazaarType;
    page: number;
    forceUpdate: () => void;
    styles: typeof defaultStyles;
}

export const PAGE_SIZE = 20;

const TRANSLATIONS: { [key in MyProductsStatusType]: React.ReactElement<FormattedMessage> } = {
    ALL_WITHOUT_DELETED: <FormattedMessage id='BAZAAR_MY_PRODUCTS_ALL_WITHOUT_DELETED' />,
    ACTIVE: <FormattedMessage id='BAZAAR_MY_PRODUCTS_ACTIVE' />,
    NOT_REPLIED: <FormattedMessage id='BAZAAR_MY_PRODUCTS_NOT_REPLIED' />,
    COULD_UPDATE: <FormattedMessage id='BAZAAR_MY_PRODUCTS_COULD_UPDATE' />,
    EXPIRED: <FormattedMessage id='BAZAAR_MY_PRODUCTS_EXPIRED' />,
    STOPPED_OR_RESERVED: <FormattedMessage id='BAZAAR_MY_PRODUCTS_STOPPED_OR_RESERVED' />,
    BLOCKED: <FormattedMessage id='BAZAAR_MY_PRODUCTS_BLOCKED' />,
    NOT_ACTIVE: <FormattedMessage id='BAZAAR_MY_PRODUCTS_NOT_ACTIVE' />,
    DELETED: <FormattedMessage id='BAZAAR_MY_PRODUCTS_DELETED' />,
    ALL: <FormattedMessage id='BAZAAR_MY_PRODUCTS_ALL' />,
};

const categoryOrder: MyProductsStatusType[] = [
    MyProductsStatusType.ALL_WITHOUT_DELETED,
    MyProductsStatusType.ACTIVE,
    MyProductsStatusType.NOT_REPLIED,
    MyProductsStatusType.COULD_UPDATE,
    MyProductsStatusType.EXPIRED,
    MyProductsStatusType.STOPPED_OR_RESERVED,
    MyProductsStatusType.BLOCKED,
    MyProductsStatusType.NOT_ACTIVE,
    MyProductsStatusType.DELETED,
    MyProductsStatusType.ALL,
];

const MyTableView: React.FC<Props> = (props: Props, { api, userStore, site } = {}) => {
    const parsedSearchParams = queryString.parse(props.location.search);
    const { myBazaarType, status, styles, page } = props;

    const [searchValue, setSearchValue] = useState('');
    const [statusFilters, setStatusFilters] = useState(new Set<string>());

    const ref = useRef(null);

    const [deleteProducts] =
        useMutation<SellerDeleteProductsInput, SellerDeleteProductsMutationVariables>(deleteProductsMutation);
    const [repostProducts] = useMutation<SellerRepostAllProductsMutation>(repostAllProductsMutation);
    const [unstarProducts] =
        useMutation<BuyerUnstarProductsMutation, BuyerUnstarProductsMutationVariables>(viewerUnstarProductsMutation);

    const after = page > 1 ? btoa(`arrayconnection:${(page - 1) * PAGE_SIZE - 1}`) : '';

    const { data, loading } = useQuery<MyProductsQuery, MyProductsQueryVariables>(query, {
        variables: {
            after,
            first: PAGE_SIZE,
            status,
            myBazaar: myBazaarType,
            search: parsedSearchParams.search || undefined,
        },
    });

    const superCategoryStore = new SuperCategoryStore({ api, userStore });
    const bazaarStore = new BazaarStore({ api, userStore });
    const createEditProductViewModel = new CreateEditProductViewModel({
        productStore: bazaarStore.productStore,
        superCategoryStore,
        site,
    });

    if (statusFilters.size === 0) {
        statusFilters.add(status ? status : MyProductsStatusType.ALL_WITHOUT_DELETED);
    }

    let title = <FormattedMessage id='BAZAAR_MY_PRODUCTS' />;
    if (myBazaarType === MyBazaarType.SHOPPING) {
        title = <FormattedMessage id='BAZAAR_MY_SHOPPING' />;
    } else if (myBazaarType === MyBazaarType.WISHLIST) {
        title = <FormattedMessage id='BAZAAR_MY_WISHLIST' />;
    }

    if (!data || !data.productsDb || !data.productsDb.edges) {
        return <LoadingWrapperView loading={loading}></LoadingWrapperView>;
    }

    const products = data.productsDb.edges!;

    const activeFilterFields = () => {
        let result: React.ReactChild[] | string = '';
        const myProductsStatusTypeSet = new Set(Object.keys(MyProductsStatusType));

        if (statusFilters.size) {
            result = Array.from(statusFilters).map(name => {
                if (myProductsStatusTypeSet.has(name)) {
                    return (
                        <span key={name}>
                            {TRANSLATIONS[name as MyProductsStatusType]}
                            &nbsp;
                        </span>
                    );
                }
                return '';
            });
        }
        return result;
    };

    const onSubmit = (values: string[]) => {
        if (myBazaarType === MyBazaarType.SELLING) {
            deleteProducts({
                variables: {
                    input: {
                        productIds: values,
                    },
                },
            }).then(props.forceUpdate);
        } else if (myBazaarType === MyBazaarType.SHOPPING) {
            buyersDelete(values).then(props.forceUpdate);
        } else if (myBazaarType === MyBazaarType.WISHLIST) {
            unstarProducts({
                variables: {
                    input: {
                        productIds: values,
                    },
                },
            }).then(() => {
                props.forceUpdate();
            });
        }
    };

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.target.value);
    };

    const onSubmitSearch = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        props.history.push({
            pathname: props.location.pathname,
            search: `?${queryString.stringify({
                ...queryString.parse(props.location.search),
                search: searchValue,
            })}`,
        });
    };

    const filterFields = () => {
        return categoryOrder.map(category => (
            <FieldView
                key={category}
                name={TRANSLATIONS[category]}
                selected={statusFilters.has(category)}
                pending={statusFilters.has(category)}
                onClick={toggleField(category)}
            />
        ));
    };

    const confirmFilter = () => {
        navigate(Array.from(statusFilters));
    };

    const clearFilter = () => {
        clear();
        navigate();
    };

    const clear = () => {
        setStatusFilters(new Set());
    };

    const toggleField = (value: string) => () => {
        const newStatusFilters = new Set<string>();
        if (!statusFilters.has(value)) {
            newStatusFilters.add(value);
        }
        setStatusFilters(newStatusFilters);
    };

    const repostAll = () => {
        return repostProducts({ variables: { input: {} } }).then(props.forceUpdate);
    };

    const buyersDelete = (productIds: string[]) => {
        return bazaarStore.buyersDelete(productIds);
    };

    const navigate = (selectedFields?: string[]) => {
        let status;
        if (selectedFields) {
            status = [...selectedFields];
        }

        props.history.push({
            pathname: props.location.pathname,
            search: `?${queryString.stringify({
                ...queryString.parse(props.location.search),
                status,
                page: page ? 1 : undefined,
            })}`,
        });
    };

    return (
        <div className={styles.pageWrapper}>
            <FormattedMessage id='BAZAAR_PRODUCT_COUNT' values={{ count: data.productsDb.totalCount }}>
                {message => (
                    <h2 className={styles.secondaryTitle}>
                        {title}
                        <span className={styles.element}>{` (${data?.productsDb?.totalCount || 0} ${message})`}</span>
                    </h2>
                )}
            </FormattedMessage>
            <div className={styles.actionsBar}>
                {!myBazaarType || myBazaarType === MyBazaarType.SELLING ? (
                    <FilterView
                        style={{
                            wrapper: styles.filterViewWrapper,
                            onlyDesktop: styles.filterViewOnlyDesktop,
                            button: styles.filterViewButton,
                            container: styles.filterViewContainer,
                            text: styles.filterViewText,
                            open: styles.filterViewOpen,
                        }}
                        title={<FormattedMessage id='BAZAAR_FILTER_TYPE_TITLE' />}
                        hideSearch={true}
                        fields={filterFields()}
                        activeFilterFields={activeFilterFields()}
                        confirm={confirmFilter}
                        clear={clearFilter}
                    />
                ) : null}
                <form onSubmit={onSubmitSearch}>
                    <SearchInput
                        style={{
                            searchInput: searchInputStyles.searchInput,
                            iconWrapper: searchInputStyles.iconWrapper,
                            input: searchInputStyles.input,
                        }}
                        onChange={onChange}
                    />
                </form>
                {myBazaarType === MyBazaarType.SELLING ? (
                    <ActionButtonView
                        styles={tableActionButtonStyles}
                        icon={<SVGIcon style={{ image: styles.icon }} src={refresh} />}
                        onClick={repostAll}
                    >
                        <FormattedMessage id='BAZAAR_UPDATE_ALL' />
                    </ActionButtonView>
                ) : null}
                <HideForUnpaidVendor>
                    <ActionButtonView
                        onClick={() => {
                            const currentRef = ref.current;
                            if (currentRef) {
                                currentRef.show({
                                    form: createEditProductViewModel.setupForm(),
                                });
                            }
                            return Promise.resolve();
                        }}
                        styles={tableActionButtonStylesDesktop}
                        icon={<SVGIcon style={{ image: styles.icon }} src={newArticle} />}
                    >
                        <FormattedMessage id='BAZAAR_ADD_PRODUCT' />
                    </ActionButtonView>
                </HideForUnpaidVendor>
                <CreateEditProductView
                    innerRef={(innerRef: any) => {
                        ref.current = innerRef;
                    }}
                    createEditProductViewModel={createEditProductViewModel}
                />
            </div>
            <ProductsTableView
                data={data}
                page={page}
                pageSize={PAGE_SIZE}
                myBazaarType={myBazaarType}
                loading={loading}
                onSubmit={onSubmit}
            />
        </div>
    );
};

MyTableView.contextTypes = {
    api: PropTypes.instanceOf(FourNetsApi),
    site: PropTypes.instanceOf(Site),
    userStore: PropTypes.instanceOf(UserStore),
};

export default Styled(defaultStyles)(withRouter(MyTableView));
